import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomSearch from "../../../Components/CustomSearch/CustomSearch";
import CustomDropDown from "../../../Components/CustomDropDown/CustomDropDown";
import TableContainer from "../../../Components/TableContainer/TableContainer";
import CustomerCardView from "../../../Components/CustomerCardView/CustomerCardView";
import { useNavigate } from "react-router-dom";
import {
  collection,
  updateDoc,
  doc,
  deleteDoc,
  getDoc,
  orderBy,
  query,
  onSnapshot,
  where,
  getDocs,
} from "firebase/firestore";
import { DATABASE, FUNCTIONS } from "../../../Config/firebase";
import { httpsCallable } from "firebase/functions";
import useDebounce from "../../../Components/useDebounce/useDebounce";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import DOCUMENT_ICON from "../../../Assets/Images/Common/documentation.png";
import ProfileImageWithFallback, {
  LIST_PROFILE_MAX_EDGE,
} from "../../../Components/ProfileImageWithFallback/ProfileImageWithFallback";
import CheckIcon from "@mui/icons-material/Check";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ViewLedgersModel from "./ViewLedgersModel";
import { dolorFormatAmount } from "../../../Utils/Services";
import { formatAppVersion, formatDevice } from "../../../Utils/appVersionAdoptionUtils";

/** Extrai o timestamp mais recente de yield_toggle_history (opt-in Yield = atividade recente) */
const getLatestYieldToggleTimestamp = (history) => {
  if (!Array.isArray(history) || history.length === 0) return null;
  let latest = null;
  history.forEach((entry) => {
    const ts = entry?.timestamp ?? (entry?.date ? new Date(entry.date).getTime() : null);
    if (ts && (!latest || ts > latest)) latest = ts;
  });
  return latest ? { toDate: () => new Date(latest), toMillis: () => latest } : null;
};

/** Retorna a data mais recente entre last_seen, last_activity e yield_toggle */
const getEffectiveLastActivity = (lastSeen, lastActivity, yieldHistory) => {
  const yieldTs = getLatestYieldToggleTimestamp(yieldHistory);
  const dates = [lastSeen, lastActivity, yieldTs].filter(Boolean).map((d) =>
    d?.toDate ? d.toDate() : d?.toMillis ? new Date(d.toMillis()) : new Date(d)
  );
  if (dates.length === 0) return null;
  return dates.reduce((max, d) => (d > max ? d : max));
};

/** Sanitiza objeto para navigation state — remove funções (toDate, toMillis) que causam DataCloneError */
const sanitizeForNavigation = (obj) => {
  if (obj == null) return obj;
  if (typeof obj === "function") return undefined;
  if (Array.isArray(obj)) return obj.map(sanitizeForNavigation);
  if (obj && typeof obj === "object" && (obj.toDate || obj.toMillis)) {
    try {
      const date = obj.toDate ? obj.toDate() : new Date(obj.toMillis());
      return date?.toISOString ? date.toISOString() : date?.getTime?.() ?? null;
    } catch {
      return null;
    }
  }
  if (typeof obj === "object") {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "function") continue;
      out[k] = sanitizeForNavigation(v);
    }
    return out;
  }
  return obj;
};

const CustomerManagement = () => {
  const [pageSize, setPageSize] = useState(50);
  const navigate = useNavigate();
  const [customerList, setCustomerList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchCustmer, setSearchCustmer] = useState("");
  const debouncedSearch = useDebounce(searchCustmer, 500);
  const [isStatus, setIsStatus] = useState(1); // Default to "Approved" (status 1)
  const [isCategoryActive, setIsCategoryActive] = useState(0); // Default: Individual (0)
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLedgersModel, setViewLedgersModel] = useState(false);
  const [selectCustomerLedgersID, setSelectCustomerLedgersID] = useState(null);
  const [lastActivityMap, setLastActivityMap] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  
  // Detect mobile device (breakpoint at 960px)
  const isMobile = useMediaQuery("(max-width: 960px)");

  const cellEllipsisSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
  };

  const columns = [
    {
      field: "rowid",
      headerName: "No.",
      width: 60,
      minWidth: 60,
      filterable: false,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      hideOnMobile: true,
    },
    {
      hideOnMobile: true,
      field: "profile_image",
      headerName: "Profile",
      width: 60,
      minWidth: 60,
      filterable: false,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,
            minHeight: 40,
            maxWidth: 40,
            maxHeight: 40,
            flexShrink: 0,
            overflow: "hidden",
            borderRadius: "10px",
            lineHeight: 0,
          }}
        >
          <ProfileImageWithFallback
            src={params?.row?.profile_image || params?.row?.profile_img}
            alt="profile_photo"
            width="40px"
            height="40px"
            objectFit="cover"
            iconColor="text.primary"
            maxIntrinsicEdge={LIST_PROFILE_MAX_EDGE}
            style={{
              width: "40px",
              height: "40px",
              minWidth: 0,
              minHeight: 0,
              maxWidth: "40px",
              maxHeight: "40px",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      minWidth: 132,
      width: 132,
      filterable: false,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { status, doc_id } = params?.row;
        const actionButtons = [];

        if (status === 1) {
          // Edit (caneta) e Reset Yield Toggle foram movidos para a tela
          // View Customer para desafogar a coluna Actions. Aqui fica só o View.
          actionButtons.push(
            <Tooltip title="View" key="view">
              <IconButton
                sx={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                  background:
                    "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  },
                }}
                onClick={() =>
                  navigate("/admin/viewcustomer", {
                    state: { customData: sanitizeForNavigation(params?.row), page },
                  })
                }
              >
                <RemoveRedEyeIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>
          );
        } else if (status === 2 || status === 3) {
          actionButtons.push(
            <Tooltip title="UnBlock" key="UnBlock">
              <IconButton
                sx={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                  background: "red",
                  "&:hover": { background: "#c62828" },
                }}
                onClick={() =>
                  handleUpdateApproveRejectBlockUnBlockStatus(doc_id, 1)
                }
              >
                <PersonOffIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>,
            <Tooltip title="View" key="view">
              <IconButton
                sx={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                  background:
                    "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  },
                }}
                onClick={() =>
                  navigate("/admin/viewcustomer", {
                    state: {
                      customData: sanitizeForNavigation(params?.row),
                      page,
                    },
                  })
                }
              >
                <RemoveRedEyeIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>
          );
        } else if (status === 0) {
          actionButtons.push(
            <Tooltip title="View" key="view">
              <IconButton
                sx={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                  background:
                    "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  },
                }}
                onClick={() =>
                  navigate("/admin/viewcustomer", {
                    state: { customData: sanitizeForNavigation(params?.row), page },
                  })
                }
              >
                <RemoveRedEyeIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>,
            <Tooltip title="Approve Request" key="approve">
              <IconButton
                sx={{
                  background: "#4caf50",
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                  "&:hover": { background: "#388e3c" },
                }}
                onClick={() =>
                  handleUpdateApproveRejectBlockUnBlockStatus(doc_id, 1)
                }
              >
                <CheckIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>,
            <Tooltip title="Reject Request" key="reject">
              <IconButton
                sx={{
                  background: "red",
                  "&:hover": { background: "#c62828" },
                  width: "30px",
                  height: "30px",
                  borderRadius: "4px",
                }}
                onClick={() =>
                  handleUpdateApproveRejectBlockUnBlockStatus(doc_id, 2)
                }
              >
                <CloseIcon sx={{ color: "#fff" }} />
              </IconButton>
            </Tooltip>
          );
        }
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
            }}
          >
            {actionButtons}
          </Box>
        );
      },
    },
    {
      field: "full_name",
      headerName: "Client",
      width: 200,
      minWidth: 180,
      flex: 1,
      filterable: true,
      sortable: true,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const name = params?.row?.full_name || "--";
        const email = params?.row?.email || "";
        const isFrozen = params?.row?.account_operations_frozen === true;
        return (
          <Tooltip title={email ? `${name}\n${email}` : name}>
            <Box sx={{ lineHeight: 1.35, minWidth: 0, width: "100%", textAlign: "left", py: 0.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                <Typography variant="body2" sx={{ ...cellEllipsisSx, fontWeight: 600, fontSize: 14, color: "text.primary" }}>
                  {name}
                </Typography>
                {isFrozen ? (
                  <Chip
                    label="Frozen"
                    size="small"
                    sx={{ height: 18, fontSize: 10, color: "#fff", backgroundColor: "#0288d1", "& .MuiChip-label": { px: 0.75 } }}
                  />
                ) : null}
              </Box>
              {email ? (
                <Typography variant="caption" sx={{ ...cellEllipsisSx, fontSize: 13, color: "text.secondary", display: "block" }}>
                  {email}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "user_type",
      headerName: "Type / App / Device",
      minWidth: 130,
      width: 130,
      hideOnMobile: true,
      sortable: true,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const userType =
          params?.row?.user_type === 0
            ? "Individual"
            : params?.row?.user_type === 1
            ? "Corporate"
            : "--";
        const hasVersion = params?.row?.app_build_number != null;
        const appLabel = formatAppVersion(params?.row);
        // Device: modelo do aparelho (app_device_model, +37) ou plataforma (app_platform).
        const deviceLabel = hasVersion ? formatDevice(params?.row) : "";
        return (
          <Tooltip
            title={
              hasVersion
                ? `${userType}\n${appLabel}\n${deviceLabel}`
                : `${userType}\nApp version not reported yet`
            }
          >
            {/* 3 linhas (tipo / versão / plataforma) dentro dos 52px do row padrão: lineHeight 1.2 e sem py. */}
            <Box sx={{ lineHeight: 1.2, minWidth: 0, width: "100%", textAlign: "left" }}>
              <Typography variant="body2" sx={{ ...cellEllipsisSx, fontSize: 13, color: "text.primary" }}>
                {userType}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ...cellEllipsisSx, fontSize: 12, color: hasVersion ? "text.secondary" : "#999", display: "block" }}
              >
                {appLabel}
              </Typography>
              {hasVersion && deviceLabel !== "--" && (
                <Typography
                  variant="caption"
                  sx={{ ...cellEllipsisSx, fontSize: 12, color: "text.secondary", display: "block" }}
                >
                  {deviceLabel}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "payout_mode",
      headerName: "Payout",
      minWidth: 96,
      width: 96,
      filterable: false,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      hideOnMobile: true,
      renderCell: (params) => {
        const isFiat = params?.row?.payout_mode === "fiat_manual";
        return (
          <Tooltip
            title={
              isFiat
                ? "Paid in FIAT manually by Fiddux — Auto-Pay disabled, withdraws without crypto wallet."
                : "Crypto withdrawal (wallet required, Auto-Pay eligible)."
            }
          >
            <Chip
              icon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
              label={isFiat ? "FIAT" : "Crypto"}
              size="small"
              color={isFiat ? "warning" : "success"}
              variant="outlined"
              sx={{ height: 24 }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "net_balance",
      headerName: "Net Balance",
      minWidth: 120,
      width: 120,
      hideOnMobile: true,
      renderCell: (params) => {
        const balance = params?.row?.net_balance || 0;
        const formattedBalance = dolorFormatAmount(balance);

        return (
          <Tooltip title={formattedBalance}>
            <span>{`$${formattedBalance}`}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "ledgers",
      headerName: "Ledgers",
      minWidth: 50,
      renderCell: (params) => {
        const { doc_id } = params?.row;
        return (
          <Tooltip title="View" key="view">
            <IconButton
              sx={{
                width: "30px",
                height: "30px",
                borderRadius: "4px",
                background:
                  "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                },
              }}
              onClick={() => handleOnChangeLedgers(doc_id)}
            >
              <RemoveRedEyeIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "last_seen",
      headerName: "Last Seen / Activity",
      minWidth: 160,
      width: 160,
      hideOnMobile: true,
      sortable: true,
      cellClassName: "leftAlign",
      // Ordena numericamente pela data mais recente (last_seen, com fallback para last_activity)
      valueGetter: (value, row) => {
        const raw = row?.last_seen || row?.last_activity;
        if (!raw) return 0;
        const date = raw?.toDate ? raw.toDate() : new Date(raw);
        return date.getTime();
      },
      renderCell: (params) => {
        const lastSeen = params?.row?.last_seen;
        const lastActivity = params?.row?.last_activity;

        // ----- Linha 1: Last Seen (relativo, ex.: "3h ago" / "Online") -----
        const effectiveDate = lastSeen || lastActivity;
        let seenLabel = "Never";
        let seenColor = "#999";
        let seenBold = 400;
        let seenTooltip = "Never seen";
        if (effectiveDate) {
          const date = effectiveDate.toDate ? effectiveDate.toDate() : new Date(effectiveDate);
          const isFromTransaction = !lastSeen && lastActivity;
          const now = new Date();
          const diffMs = now - date;
          const diffMins = Math.floor(diffMs / (1000 * 60));
          const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

          if (diffMins < 5) seenLabel = "Online";
          else if (diffMins < 60) seenLabel = `${diffMins}m ago`;
          else if (diffHours < 24) seenLabel = `${diffHours}h ago`;
          else if (diffDays < 365) seenLabel = `${diffDays}d ago`;
          else seenLabel = `${Math.floor(diffDays / 365)}y ago`;

          seenColor = "#5fdba7";
          if (diffMins >= 5 && diffDays < 1) seenColor = "#81c784";
          if (diffDays >= 1 && diffDays < 7) seenColor = "#fff176";
          if (diffDays >= 7 && diffDays < 30) seenColor = "#ff9800";
          if (diffDays >= 30) seenColor = "#f44336";
          seenBold = diffMins < 5 ? 600 : 400;

          const fullDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
          seenTooltip = isFromTransaction ? `${fullDate} (from last transaction)` : fullDate;
        }

        // ----- Linha 2: Last Activity (data completa DD/MM/YYYY HH:mm) -----
        let activityLabel = "No activity";
        let activityColor = "#999";
        let activityTooltip = "No activity recorded";
        if (lastActivity) {
          const date = lastActivity.toDate ? lastActivity.toDate() : new Date(lastActivity);
          activityLabel = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
          const now = new Date();
          const daysAgo = Math.floor((now - date) / (1000 * 60 * 60 * 24));
          activityColor = "#5fdba7";
          if (daysAgo > 30) activityColor = "#ff9800";
          if (daysAgo > 60) activityColor = "#f44336";
          activityTooltip = `${daysAgo} days ago`;
        }

        return (
          <Tooltip title={`Seen: ${seenTooltip}\nActivity: ${activityTooltip}`}>
            <Box sx={{ lineHeight: 1.3, minWidth: 0, width: "100%", textAlign: "left", py: 0.25 }}>
              <Typography
                variant="body2"
                sx={{ ...cellEllipsisSx, fontSize: 13, fontWeight: seenBold, color: seenColor }}
              >
                {seenLabel}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ...cellEllipsisSx, fontSize: 12, color: activityColor, display: "block" }}
              >
                {activityLabel}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 85,
      width: 90,
      filterable: false,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { status } = params?.row;
        const chipSx = { color: "#fff", fontSize: "0.75rem", height: 22 };
        const renderStatusChip = () => {
          switch (status) {
            case 0:
              return (
                <Chip
                  size="small"
                  label="Pending"
                  sx={{ ...chipSx, backgroundColor: "#FFC107" }}
                />
              );
            case 1:
              return (
                <Chip
                  size="small"
                  label="Approved"
                  sx={{ ...chipSx, backgroundColor: "#5fdba7" }}
                />
              );
            case 2:
              return (
                <Chip
                  size="small"
                  label="Rejected"
                  sx={{ ...chipSx, backgroundColor: "darkred" }}
                />
              );
            case 3:
              return (
                <Chip
                  size="small"
                  label="Blocked"
                  sx={{ ...chipSx, backgroundColor: "darkred" }}
                />
              );
            default:
              return null;
          }
        };

        return renderStatusChip();
      },
    },
  ];
  // ============= END TABLE COLUMN ===============
  // ============= START HANDLE_ALL_STATUS_LIST ===============
  const isStatusList = [
    { name: "All", id: null },
    { id: 0, name: "Pending", label: "Pending" },
    { id: 1, name: "Approved", label: "Approved" },
    { id: 2, name: "Blocked", label: "Blocked" },
  ];
  const isCategoryActiveList = [
    { name: "All", id: null },
    { id: 0, name: "Individual Customer", label: "Individual Customer" },
    { id: 1, name: "Corporate Customer", label: "Corporate Customer" },
  ];
  
  // Fetch last activity for all users (once on mount)
  useEffect(() => {
    const fetchLastActivity = async () => {
      try {
        // Get ALL transactions (deposits and withdrawals only - EXCLUDE performance which is request_type 2)
        const transQuery = query(
          collection(DATABASE, "transection_requests"),
          where("request_type", "in", [0, 1]) // 0 = deposit, 1 = withdrawal
        );
        const transSnapshot = await getDocs(transQuery);
        
        // Build a map of user_id -> last activity date
        const activityMap = {};
        transSnapshot.forEach(doc => {
          const data = doc.data();
          const userId = data.user_id;
          const date = data.date;
          const amount = data.total_amount || 0;
          
          // FILTER OUT monthly performance (withdrawal with amount = 0)
          // Real deposits/withdrawals always have amount > 0
          if (userId && date && amount > 0) {
            // Keep only the most recent date for each user
            if (!activityMap[userId] || date.toMillis() > activityMap[userId].toMillis()) {
              activityMap[userId] = date;
            }
          }
        });
        
        setLastActivityMap(activityMap);
        // Last Activity Map loaded successfully
      } catch (error) {
        console.error("Error fetching last activity:", error);
      }
    };
    
    fetchLastActivity();
  }, []); // Run only once when component mounts
  
  useEffect(() => {
    const unsubscribe = getCustomerList(
      page,
      pageSize,
      debouncedSearch,
      isStatus,
      isCategoryActive
    );
    return () => unsubscribe();
  }, [debouncedSearch, page, isStatus, isCategoryActive, lastActivityMap]);
  const getCustomerList = (
    currentPage = 0,
    pageSize = 50,
    debouncedSearch = "",
    statusFilter = null,
    categoryFilter = null
  ) => {
    setIsLoading(true);
    const q = query(
      collection(DATABASE, "users"),
      orderBy("registered_date", "desc")
    );
    return onSnapshot(
      q,
      (querySnapshot) => {
        const allData = querySnapshot?.docs
          .filter((doc) => doc?.data()?.role === 1)
          .map((doc, index) => {
            const data = doc.data();
            const lastActivityFromTx = lastActivityMap[doc.id] || null;
            const lastSeen = data.last_seen || null;
            const yieldHistory = data.yield_toggle_history || [];
            const effective = getEffectiveLastActivity(lastSeen, lastActivityFromTx, yieldHistory);
            const effectiveAsTimestamp = effective
              ? { toDate: () => effective, toMillis: () => effective.getTime() }
              : null;
            return {
              rowid: index + 1,
              id: doc.id,
              ...data,
              last_activity: effectiveAsTimestamp || lastActivityFromTx,
              last_seen: lastSeen || effectiveAsTimestamp,
            };
          });

        // Contador de pendentes (independente do filtro atual) para o alerta no topo
        const pendingTotal = allData?.filter((customer) => customer?.status === 0)?.length || 0;
        setPendingCount(pendingTotal);

        const trimmedSearch = debouncedSearch?.trim().toLowerCase();
        const filteredData = allData?.filter((customer) => {
          const email = customer?.email?.trim()?.toLowerCase() || "";
          const name = customer?.full_name?.trim()?.toLowerCase() || "";
          const userId = (customer?.doc_id || customer?.id || "").toLowerCase();
          const net_balance =
            customer?.net_balance != null
              ? customer?.net_balance.toString().trim()
              : "";
          return (
            email.includes(trimmedSearch) ||
            name.includes(trimmedSearch) ||
            userId.includes(trimmedSearch) ||
            net_balance?.includes(trimmedSearch)
          );
        });

        const statusFilteredData =
          statusFilter === null
            ? filteredData
            : filteredData.filter(
              (customer) => customer?.status === statusFilter
            );
        const finalData =
          categoryFilter === null
            ? statusFilteredData
            : statusFilteredData.filter(
              (customer) => customer?.user_type === categoryFilter
            );

        // Sort by full_name A-Z (ascending)
        const sortedData = finalData.sort((a, b) => {
          const nameA = (a?.full_name || "").toLowerCase().trim();
          const nameB = (b?.full_name || "").toLowerCase().trim();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });

        // Add sequential row numbers AFTER filtering and sorting
        const dataWithRowNumbers = sortedData.map((item, index) => ({
          ...item,
          rowid: index + 1, // Sequential number based on filtered results
        }));

        setTotalCount(dataWithRowNumbers.length);
        setCustomerList(
          dataWithRowNumbers.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
        );
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching customers:", error);
        setIsLoading(false);
      }
    );
  };
  const handleUpdateStatusCategory = (id) => {
    const parsed = id === null || id === undefined ? null : (typeof id === "string" ? parseInt(id, 10) : id);
    setIsCategoryActive(Number.isNaN(parsed) ? null : parsed);
    setPage(0);
  };
  const handleUpdateActiveUnVerifyBlockStatus = (id) => {
    setIsStatus(id === null ? null : id);
    setPage(0);
  };
  const handleUpdateApproveRejectBlockUnBlockStatus = async (
    docId,
    newStatus
  ) => {
    setIsLoading(true);
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${newStatus === 1 ? "approve" : newStatus === 2 ? "Block" : "Reject"
        } this customer`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#46adf2",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await updateDoc(doc(DATABASE, "users", docId), { status: newStatus });
        toast.success(
          newStatus === 1 ? "Approved!" : "Blocked!",
          `Customer has been ${newStatus === 1 ? "accepted" : "Blocked"}.`,
          "success"
        );
        setPage(0);
        getCustomerList(
          page,
          pageSize,
          debouncedSearch,
          isStatus,
          isCategoryActive
        );
      } catch (error) {
        console.error("Error updating customer status:", error);
        toast.success("Error!", "Something went wrong. Try again!", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResetYieldToggleCount = async (docId, userEmail, userName) => {
    const result = await Swal.fire({
      title: "Reset Yield Toggle Count?",
      text: `This will reset the monthly toggle count for ${userName || userEmail || "this user"}. They will be able to toggle Yield products again.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#46adf2",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes, Reset",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await updateDoc(doc(DATABASE, "users", docId), {
          yield_toggles_this_month: 0,
        });
        toast.success(
          "Toggle Count Reset!",
          `Yield toggle count has been reset for ${userName || userEmail || "this user"}.`,
          "success"
        );
        // Refresh the list to show updated data
        getCustomerList(
          page,
          pageSize,
          debouncedSearch,
          isStatus,
          isCategoryActive
        );
      } catch (error) {
        console.error("Error resetting toggle count:", error);
        toast.success("Error!", "Something went wrong. Try again!", "error");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const checkUserReferences = async (userId) => {
    const collections = [
      { name: "transection_requests", field: "user_id" },
      { name: "ledgers", field: "user_id" },
      { name: "monthly_closings", field: "user_id" },
      { name: "monthly_grpahs", field: "user_id" },
      { name: "yearly_graph", field: "user_id" },
      { name: "notifications", field: "user_id" },
    ];

    let totalReferences = 0;
    const references = [];

    for (const collectionInfo of collections) {
      try {
        const q = query(
          collection(DATABASE, collectionInfo.name),
          where(collectionInfo.field, "==", userId)
        );
        const snapshot = await getDocs(q);
        const count = snapshot.size;
        totalReferences += count;
        if (count > 0) {
          references.push({
            collection: collectionInfo.name,
            count: count,
          });
        }
      } catch (error) {
        console.error(`Error checking ${collectionInfo.name}:`, error);
      }
    }

    return { totalReferences, references };
  };

  const handleDeleteUser = async (docId, userEmail, userName) => {
    // Step 1: Password prompt
    const { value: password } = await Swal.fire({
      title: "Admin Password Required",
      text: "Please enter the admin password to delete this user:",
      input: "password",
      inputPlaceholder: "Enter password",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Continue",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) {
          return "Password is required";
        }
      },
    });

    if (!password) {
      return;
    }

    // Step 1.5: Validate password via Cloud Function
    try {
      const validatePassword = httpsCallable(FUNCTIONS, "validateAdminPassword");
      
      const result = await validatePassword({
        password: password.trim(),
        action: "delete-user"
      });

      if (!result.data.valid) {
        Swal.fire({
          icon: "error",
          title: "Access Denied",
          text: "Incorrect password. Please try again.",
        });
        return;
      }
    } catch (error) {
      console.error("Error validating password:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error validating password. Please try again.",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Step 1.5: Verify document exists and get correct ID
      let actualDocId = docId;
      let userDocRef = null;
      
      // Try to find user by document ID first
      try {
        userDocRef = doc(DATABASE, "users", docId);
        const userDocSnap = await getDoc(userDocRef);
        
        if (!userDocSnap.exists()) {
          // Document doesn't exist with this ID, try to find by email (only if email exists and is valid)
          if (userEmail && userEmail !== "--" && userEmail.trim() !== "") {
            console.log(`Document ${docId} not found, searching by email: ${userEmail}`);
            const emailQuery = query(
              collection(DATABASE, "users"),
              where("email", "==", userEmail)
            );
            const emailSnapshot = await getDocs(emailQuery);
            
            if (!emailSnapshot.empty) {
              actualDocId = emailSnapshot.docs[0].id;
              userDocRef = doc(DATABASE, "users", actualDocId);
              console.log(`Found user by email, document ID: ${actualDocId}`);
            } else {
              throw new Error(`User not found with ID ${docId} or email ${userEmail}`);
            }
          } else {
            // No valid email to search by, document ID should be correct
            throw new Error(`User not found with document ID ${docId}. Email is missing or invalid, cannot search by email.`);
          }
        } else {
          console.log(`User document found with ID: ${docId}`);
        }
      } catch (findError) {
        console.error("Error finding user document:", findError);
        throw new Error(`Could not find user document: ${findError.message}`);
      }

      // Step 2: Check for references using the actual document ID
      const { totalReferences, references } = await checkUserReferences(actualDocId);

      if (totalReferences > 0) {
        const referencesList = references
          .map((ref) => `${ref.collection}: ${ref.count}`)
          .join("\n");

        const result = await Swal.fire({
          title: "⚠️ User Has Data References",
          html: `This user has ${totalReferences} reference(s) in other collections:<br/><br/>${referencesList}<br/><br/>Deleting may cause data integrity issues. Are you sure you want to proceed?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#5b5d5d",
          confirmButtonText: "Yes, Delete Anyway",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
          setIsLoading(false);
          return;
        }
      }

      // Step 3: Final confirmation
      const result = await Swal.fire({
        title: "⚠️ Delete User?",
        html: `Are you sure you want to permanently delete:<br/><br/><strong>User Name:</strong> ${userName || "N/A"}<br/><strong>Email:</strong> ${userEmail || "N/A"}<br/><br/><strong>Document ID:</strong> ${actualDocId}<br/><br/>This action cannot be undone!`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#5b5d5d",
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
      });

      if (result.isConfirmed) {
        // Step 4: Delete user
        console.log("Attempting to delete user:");
        console.log("  - Document ID:", actualDocId);
        console.log("  - Email:", userEmail);
        console.log("  - Name:", userName);
        console.log("  - Reference path:", userDocRef.path);
        
        await deleteDoc(userDocRef);
        console.log("✅ User deleted successfully");

        await Swal.fire({
          title: "Deleted!",
          text: "User has been permanently deleted.",
          icon: "success",
          confirmButtonColor: "#46adf2",
        });

        toast.success("Deleted!", "User has been permanently deleted.", "success");

        // Refresh the list
        setPage(0);
        getCustomerList(
          page,
          pageSize,
          debouncedSearch,
          isStatus,
          isCategoryActive
        );
      }
    } catch (error) {
      console.error("❌ Error deleting user:", error);
      console.error("  - Error code:", error.code);
      console.error("  - Error message:", error.message);
      console.error("  - Full error:", error);
      
      let errorMessage = "Failed to delete user. Please try again.";
      
      if (error.code === "permission-denied") {
        errorMessage = "Permission denied. You may not have permission to delete users.";
      } else if (error.code === "not-found") {
        errorMessage = "User document not found. It may have already been deleted.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      await Swal.fire({
        title: "Error!",
        html: `<strong>Failed to delete user</strong><br/><br/>${errorMessage}<br/><br/>Check the browser console for more details.`,
        icon: "error",
        confirmButtonColor: "#46adf2",
      });
      toast.error("Error!", errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    getCustomerList(
      0,
      newPageSize,
      debouncedSearch,
      isStatus,
      isCategoryActive
    );
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
    getCustomerList(
      newPage,
      pageSize,
      debouncedSearch,
      isStatus,
      isCategoryActive
    );
  };
  const handleOnChangeLedgers = (userID) => {
    setSelectCustomerLedgersID(userID);
    setViewLedgersModel(true);
  };
  return (
    <>
      <Box
        sx={{
          width: "100%",
          bgcolor: "background.default", // Usa o background do tema (dark mode)
          paddingTop: { xs: '60px', sm: "0px" }
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography sx={{ fontSize: "24px", fontWeight: "500", color: "text.primary" }}>
              Customer Management
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button
                onClick={() => navigate("/admin/addcustomer")}
                sx={{
                  background:
                    "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                  color: "#fff",
                  height: "46px",
                  padding: "0px 20px",
                  textTransform: "capitalize",
                  "&:hover": {
                    background:
                      "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
                    color: "#fff",
                  },
                }}
              >
                + Add Customer
              </Button>
            </Box>
          </Grid>
          {pendingCount > 0 && isStatus !== 0 && (
            <Grid item xs={12}>
              <Box
                onClick={() => handleUpdateActiveUnVerifyBlockStatus(0)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: "1px solid #FFC107",
                  backgroundColor: "rgba(255, 193, 7, 0.12)",
                  "&:hover": { backgroundColor: "rgba(255, 193, 7, 0.2)" },
                }}
              >
                <Chip
                  label={pendingCount}
                  size="small"
                  sx={{ color: "#000", backgroundColor: "#FFC107", fontWeight: 700, height: 22 }}
                />
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  {pendingCount === 1
                    ? "1 new account request pending review"
                    : `${pendingCount} new account requests pending review`}
                  {" — click to view"}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <CustomSearch
              placeholder="Search Customer (Email, Full Name, User ID)"
              value={searchCustmer}
              onChange={(event) => setSearchCustmer(event.target.value)}
              onClear={() => setSearchCustmer("")}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomDropDown
                  sx={{ height: "46px" }}
                  placeholder={"Select Category"}
                  menuList={isCategoryActiveList}
                  value={isCategoryActive}
                  onChange={(e) => handleUpdateStatusCategory(e.target.value)}
                  getLabel={(item) => item?.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomDropDown
                  sx={{ height: "46px" }}
                  placeholder={"Select Status"}
                  menuList={isStatusList}
                  value={isStatus}
                  onChange={(e) =>
                    handleUpdateActiveUnVerifyBlockStatus(e.target.value)
                  }
                  getLabel={(item) => item?.name}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box
          sx={{
            width: "100%",
            paddingTop: "24px",
          }}
        >
          {/* Desktop: TableContainer (comportamento atual) */}
          {!isMobile && (
            <TableContainer
              rows={customerList}
              columns={columns}
              loading={isLoading}
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              handlePageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              paginationShow={true}
              initialSortModel={[{ field: 'full_name', sort: 'asc' }]}
              getRowClassName={(params) => (params?.row?.user_type === 1 ? "corporateRow" : "")}
            />
          )}
          
          {/* Mobile: CustomerCardView (nova visualização) */}
          {isMobile && (
            <CustomerCardView
              customers={customerList}
              loading={isLoading}
              onEdit={(customer) =>
                navigate("/admin/editcustomer", {
                  state: { customData: sanitizeForNavigation(customer) },
                })
              }
              onView={(customer) =>
                navigate("/admin/viewcustomer", {
                  state: { customData: sanitizeForNavigation(customer), page },
                })
              }
              onDelete={handleDeleteUser}
              onApprove={(docId) => handleUpdateApproveRejectBlockUnBlockStatus(docId, 1)}
              onReject={(docId) => handleUpdateApproveRejectBlockUnBlockStatus(docId, 2)}
              onBlock={(docId) => handleUpdateApproveRejectBlockUnBlockStatus(docId, 3)}
              onUnblock={(docId) => handleUpdateApproveRejectBlockUnBlockStatus(docId, 1)}
              onResetYieldToggle={handleResetYieldToggleCount}
              onViewLedgers={handleOnChangeLedgers}
              navigate={navigate}
              page={page}
            />
          )}
        </Box>
      </Box>
      <ViewLedgersModel
        open={viewLedgersModel}
        onClose={() => setViewLedgersModel(false)}
        selectCustomerLedgersID={selectCustomerLedgersID}
        customerList={customerList?.map((c) => ({
          id: c?.doc_id || c?.id,
          name: c?.full_name || c?.email || "—",
        }))}
        onSelectCustomer={(newId) => setSelectCustomerLedgersID(newId)}
      />
    </>
  );
};

export default CustomerManagement;
